(() => {
  if (window.KaPdpStickySubnav) return;

  class KaPdpStickySubnav {
    constructor(nav) {
      this.nav = nav;
      this.header = document.querySelector('#header, theme-header, .header-section .header');
      this.headerSection = this.header?.closest('.header-section');
      this.trigger = document.querySelector('.ba-breadcrumb');
      this.links = Array.from(nav.querySelectorAll('[data-ka-pdp-anchor]'));
      this.sections = this.links
        .map((link) => ({
          link,
          section: document.getElementById(link.hash.slice(1)),
          entry: null,
          enabled: true
        }))
        .filter((item) => item.section);

      this.headerHeight = 0;
      this.navHeight = 48;
      this.headerOffset = 0;
      this.scrollFrame = null;
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);

      this.links.forEach((link) => {
        link.addEventListener('click', (event) => this.handleAnchorClick(event, link));
      });

      this.measureDimensions();
      this.setupResizeObserver();
      this.setupVisibilityObserver();
      this.setupSectionObserver();
    }

    measureDimensions() {
      this.headerHeight = this.header?.offsetHeight || 0;
      this.navHeight = this.nav.offsetHeight || 48;
      this.updateHeaderOffset();
    }

    updateHeaderOffset() {
      const stickyTarget = this.headerSection || this.header;
      const headerIsSticky = stickyTarget && window.getComputedStyle(stickyTarget).position === 'sticky';
      const nextOffset = headerIsSticky ? this.headerHeight : 0;

      if (nextOffset === this.headerOffset) return false;

      this.headerOffset = nextOffset;
      this.nav.style.setProperty('--ka-pdp-sticky-subnav-top', `${this.headerOffset}px`);
      return true;
    }

    setupResizeObserver() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver((entries) => {
          let dimensionsChanged = false;

          entries.forEach((entry) => {
            const borderBox = Array.isArray(entry.borderBoxSize)
              ? entry.borderBoxSize[0]
              : entry.borderBoxSize;
            const height = Math.round(borderBox?.blockSize ?? entry.target.getBoundingClientRect().height);

            if (entry.target === this.header && height !== this.headerHeight) {
              this.headerHeight = height;
              dimensionsChanged = true;
            }

            if (entry.target === this.nav && height !== this.navHeight) {
              this.navHeight = height;
              dimensionsChanged = true;
            }
          });

          if (this.updateHeaderOffset()) dimensionsChanged = true;
          if (dimensionsChanged) this.setupSectionObserver();
        });

        if (this.header) this.resizeObserver.observe(this.header);
        this.resizeObserver.observe(this.nav);
      } else {
        window.addEventListener('resize', this.handleResize, { passive: true });
      }
    }

    handleResize() {
      if (this.resizeFrame) return;

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.measureDimensions();
        this.setupSectionObserver();
      });
    }

    setupVisibilityObserver() {
      if (this.trigger && 'IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          const rootTop = entry.rootBounds?.top || 0;
          const hasPassedTrigger = !entry.isIntersecting && entry.boundingClientRect.bottom <= rootTop;
          this.nav.classList.toggle('is-visible', hasPassedTrigger);
        }, { threshold: 0 });
        this.visibilityObserver.observe(this.trigger);
        return;
      }

      this.triggerScrollY = this.trigger
        ? this.trigger.getBoundingClientRect().bottom + window.scrollY
        : 150;
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      this.handleScroll();
    }

    handleScroll() {
      if (this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.nav.classList.toggle('is-visible', window.scrollY >= this.triggerScrollY);
      });
    }

    setupSectionObserver() {
      this.sectionObserver?.disconnect();

      if (!this.sections.length || !('IntersectionObserver' in window)) return;

      this.sections.forEach((item) => {
        item.enabled = item.link.offsetParent !== null;
        item.entry = null;
        if (!item.enabled) item.link.classList.remove('is-active');
      });

      const topOffset = Math.max(0, Math.round(this.headerOffset + this.navHeight + 12));
      this.sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const item = this.sections.find((sectionItem) => sectionItem.section === entry.target);
          if (item) item.entry = entry;
        });

        const activeItems = this.sections
          .filter((item) => item.enabled && item.entry?.isIntersecting)
          .sort((a, b) => {
            const aDistance = Math.abs(a.entry.boundingClientRect.top - topOffset);
            const bDistance = Math.abs(b.entry.boundingClientRect.top - topOffset);
            return aDistance - bDistance;
          });

        if (activeItems.length) this.setActiveLink(activeItems[0].link);
      }, {
        rootMargin: `-${topOffset}px 0px -55% 0px`,
        threshold: 0
      });

      this.sections.forEach((item) => {
        if (item.enabled) this.sectionObserver.observe(item.section);
      });
    }

    setActiveLink(activeLink) {
      this.links.forEach((link) => {
        link.classList.toggle('is-active', link === activeLink);
      });
    }

    handleAnchorClick(event, link) {
      if (link.offsetParent === null) return;

      const target = document.getElementById(link.hash.slice(1));
      if (!target) return;

      event.preventDefault();
      this.setActiveLink(link);

      const offset = this.headerOffset + this.navHeight + 8;
      const targetTop = target.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({
        top: targetTop,
        behavior: 'smooth'
      });
    }

    destroy() {
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.sectionObserver?.disconnect();
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
    }
  }

  window.KaPdpStickySubnav = KaPdpStickySubnav;

  const initialise = (root = document) => {
    root.querySelectorAll('[data-ka-pdp-sticky-subnav]').forEach((nav) => {
      if (nav.kaPdpStickySubnav) return;
      nav.kaPdpStickySubnav = new KaPdpStickySubnav(nav);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-ka-pdp-sticky-subnav]').forEach((nav) => {
      nav.kaPdpStickySubnav?.destroy();
    });
  });
})();
